import BrainActiveLog from './BrainActiveLog';
import {flatReportRow, isFlatReport, reportUnits, reportDollars, reportMoney, FLAT_REPORT_STAKE} from './brainFlatReporting';
import React, { useEffect, useMemo, useState } from 'react';
import BrainBacktest from './BrainBacktest';
import BrainLiveGrid from './BrainLiveGrid';
import './StrategyReports.css';
import './BrainAutoDashboard.css';
import HistoricalBrainBets from './HistoricalBrainBets';
import { brainBetAppearance } from './brainBetAppearance';
import {
  getStakingBrainPaper,
  StakingBrainPaper as StakingBrainPaperData,
  StakingBrainPaperRow,
  getStakingBrainStreams,
  getBrainUpcoming, BrainUpcoming,
  StakingBrainStream,
} from '../api/feeds';

const AU_TZ = 'Australia/Sydney';

const auDate = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: AU_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
  return get('year') + '-' + get('month') + '-' + get('day');
};

const money = (value: number | null) =>
  value == null ? '—' : new Intl.NumberFormat('en-AU', {
    style: 'currency', currency: 'AUD', minimumFractionDigits: 2,
  }).format(Number(value));

const benchmark = (value?: number | null) => value == null ? '—' : <>{value.toFixed(2)}u<small style={{display:'block',fontSize:12,fontWeight:400}}>{reportMoney(reportDollars(value))} at A$100/unit</small></>;

const percent = (value: number | null) => value == null ? '—' : (value * 100).toFixed(1) + '%';

const buttonStyle = (active = false): React.CSSProperties => ({
  padding: '7px 12px',
  borderRadius: 6,
  border: '1px solid ' + (active ? '#2d6cdf' : '#cfd4dc'),
  background: active ? '#2d6cdf' : '#fff',
  color: active ? '#fff' : '#333',
  cursor: 'pointer',
  fontWeight: 600,
});

const Stat: React.FC<{ label: string; value: React.ReactNode; color?: string }> = ({ label, value, color }) => (
  <div className="sr-tile">
    <div className="sr-tile-val" style={{color}}>{value}</div>
    <div className="sr-tile-label">{label}</div>
  </div>
);

const chip = (decision: string) => {
  const isBet = decision === 'BET';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 7px', borderRadius: 4,
      background: isBet ? '#2d6cdf' : '#eceff2', color: isBet ? '#fff' : '#555',
      fontSize: 11, fontWeight: 700,
    }}>
      {decision}
    </span>
  );
};

const CompactRow: React.FC<{ row: StakingBrainPaperRow; detailed: boolean }> = ({ row, detailed }) => {
  return (
    <div style={{
      ...brainBetAppearance(row.decision, row.result, row.status),
      display: 'grid',
      gridTemplateColumns: '170px 75px 90px 45px minmax(140px,1fr) 50px 70px 70px 95px 85px' + (detailed ? ' minmax(180px,1.2fr)' : ''),
      gap: 9, alignItems: 'center', padding: '8px 0', borderTop: '1px solid #edf0f2', fontSize: 13,
    }}>
      <span>{row.stream_name || 'Original Brain'}</span>
      <span>{row.off_at ? new Date(row.off_at).toLocaleTimeString('en-AU',{timeZone:AU_TZ,hour:'2-digit',minute:'2-digit'}) : '—'}</span>
      <span>{row.track}</span>
      <span>R{row.race}</span>
      <span>{row.runner}</span>
      <span>#{row.core_rank ?? '—'}</span>
      <span>{money(row.decision_price)}</span>
      <span>{!isFlatReport(row.stream_id) && (row.benchmark_pnl_units != null || (row.stream_name && row.applied_stake == null && row.decision === 'BET')) ? '1u' : money(row.applied_stake)}</span>
      <span>{row.status}</span>
      <span>
        {row.benchmark_pnl_units != null ? reportUnits(row.stream_id, row.benchmark_pnl_units) : row.status === 'SETTLED' ? money(row.paper_pnl_dollars) : row.status === 'PROVISIONAL' || row.status === 'PASS' ? '—' : 'Pending'}
      </span>
      {detailed && <span title={(row.decision_reasons || []).join(' | ')}>{chip(row.decision)}</span>}
    </div>
  );
};

const StakingBrainPaper: React.FC<{initialStream?:string}> = ({initialStream=''}) => {
  const [selectedStream,setSelectedStream]=useState(initialStream);
  const [streamOptions,setStreamOptions]=useState<StakingBrainStream[]>([]);
  useEffect(()=>{getStakingBrainStreams().then(d=>setStreamOptions(d.streams.filter(s=>s.state==='ACTIVE_PAPER'))).catch(()=>{});},[]);
  const today = auDate();
  const [date, setDate] = useState(today);
  const [showAllRunners, setShowAllRunners] = useState(false);
  const [data, setData] = useState<StakingBrainPaperData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const refresh = () => getStakingBrainPaper({ date, scope: 'active', view: showAllRunners ? 'all' : 'bets' })
      .then((result) => { if (!cancelled) setData(result); })
      .catch((err: any) => { if (!cancelled) setError(err?.message || 'Could not load Brain bets'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    refresh();
    const timer = window.setInterval(refresh, 60000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [date, showAllRunners]);

  const visibleRows = useMemo(()=>(data?.rows||[]).map(flatReportRow).filter(r=>!selectedStream||r.stream_id===selectedStream),[data,selectedStream]);
  const betRows = visibleRows.filter(row=>row.decision==='BET');
  const settledBets = betRows.filter((row) => row.status === 'SETTLED');
  const wins = settledBets.filter((row) => row.result === 'win').length;
  const pricedBets = settledBets.filter(row => row.paper_pnl_dollars != null && row.applied_stake != null);
  const pnl = pricedBets.reduce((sum, row) => sum + Number(row.paper_pnl_dollars || 0), 0);
  const staked = pricedBets.reduce((sum, row) => sum + Number(row.applied_stake || 0), 0);
  const pnlColor = pnl > 0 ? '#218838' : pnl < 0 ? '#c0392b' : '#20242a';

  const changeDate = (value: string) => {
    setShowAllRunners(false);
    setDate(value);
  };

  return (
    <main style={{ maxWidth: 1120, margin: '0 auto', padding: 16 }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0 }}>Staking Brain — Daily Paper Bets</h1>
          <div style={{ color: '#7b8491', marginTop: 4 }}>All active Brain streams, including upcoming provisional selections. Times are Sydney time.</div>
        </div>
        <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
          <label htmlFor="brain-date" style={{ fontWeight: 600 }}>Racing day</label>
          <input id="brain-date" type="date" value={date} onChange={(e) => changeDate(e.target.value)}
            style={{ padding: '7px 10px', border: '1px solid #cfd4dc', borderRadius: 6 }} />
          <button style={buttonStyle(false)} onClick={() => {
 const cols=['date','stream_name','off_at','track','race','runner','decision','status','decision_price','applied_stake','paper_pnl_dollars','benchmark_pnl_units'];
 const cell=(v:unknown)=>{let text=String(v??'');if(/^[=+\-@]/.test(text))text="'"+text;return '"'+text.replace(/"/g,'""')+'"';};
 const csv=[cols.join(','),...visibleRows.map(r=>cols.map(k=>cell((r as unknown as Record<string,unknown>)[k])).join(','))].join('\r\n');
 const url=URL.createObjectURL(new Blob([csv],{type:'text/csv'}));const a=document.createElement('a');a.href=url;a.download='brain-'+(selectedStream||'all-streams')+'-'+date+'.csv';a.click();URL.revokeObjectURL(url);
}}>Download bets</button>
        </div>
      </header>

      <div className="brain-filter"><label>Brain stream<select value={selectedStream} onChange={e=>setSelectedStream(e.target.value)}><option value="">All active streams · sorted by time</option>{streamOptions.map(st=><option key={st.stream_id} value={st.stream_id}>{st.name}</option>)}</select></label></div>
      <p aria-label="Bet colour key"><span style={{color:'#16803c'}}>Green: pending</span> / <strong style={{color:'#111'}}>Black bold: won</strong> / <span style={{color:'#b66b6b'}}>Soft red: lost</span>. Results refresh every minute.</p>
      {error && <p style={{ color: '#c0392b' }}>{error}</p>}

      {data && (
        <>
          <section className="sr-tiles brain-daily-metrics">
            <Stat label="Locked bets" value={String(betRows.length)} />
            <Stat label="Provisional" value={String(visibleRows.filter(r=>r.decision==='PROVISIONAL').length)} />
            <Stat label="Settled" value={String(settledBets.length)} />
            <Stat label="Wins" value={String(wins)} />
            <Stat label="Strike rate" value={settledBets.length ? percent(wins / settledBets.length) : '—'} />
            <Stat label="Simulated P&L ($)" value={money(pnl)} color={pnlColor} />
            <Stat label="Simulated ROI" value={staked ? (pnl / staked * 100).toFixed(1) + '%' : '—'} color={pnlColor} />
          </section>

          <p>Provisional selections are excluded from bet totals and results. Daily Brain (3 / Saturday 10) uses flat A$100 simulated stakes across all dates. Totals include its dollar results and the frozen control’s paper stakes. Unpriced results are excluded from P&amp;L and ROI.</p>
          <section className="brain-daily-table" style={{padding:'0 12px'}}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '170px 75px 90px 45px minmax(140px,1fr) 50px 70px 70px 95px 85px' + (showAllRunners ? ' minmax(180px,1.2fr)' : ''),
              gap: 9, color: '#858d98', fontSize: 11, textTransform: 'uppercase', padding: '9px 0',
            }}>
              <span>Brain stream</span><span>Time</span><span>Track</span><span>Race</span><span>Runner</span><span>Core</span>
              <span>Price</span><span>Stake</span><span>Status</span><span>P&L</span>
              {showAllRunners && <span>Bet / pass status</span>}
            </div>
            {!loading && visibleRows.length === 0 && (
              <p style={{ color: '#7b8491', padding: '12px 0' }}>
                No bets were recorded for this racing day under the active paper version.
              </p>
            )}
            {visibleRows.map((row, index) => (
              <CompactRow key={[row.date, row.track, row.race, row.runner, index].join('|')} row={row} detailed={showAllRunners} />
            ))}
          </section>

          <div style={{ marginTop: 12 }}>
            <button
              style={buttonStyle(showAllRunners)}
              disabled={loading}
              onClick={() => setShowAllRunners((value) => !value)}
            >
              {loading ? 'Loading…' : showAllRunners ? 'Hide all runners' : 'Show all runners and BET/PASS status'}
            </button>
            {showAllRunners && (
              <span style={{ marginLeft: 10, color: '#7b8491', fontSize: 12 }}>
                Full runner data was fetched only for {date}.
              </span>
            )}
          </div>
        </>
      )}
      {loading && !data && <p>Loading bets…</p>}
    </main>
  );
};

const BrainStreams: React.FC = () => {
  const [upcoming,setUpcoming]=useState<BrainUpcoming|null>(null);
  const [upcomingError,setUpcomingError]=useState('');
  const [selectedStream,setSelectedStream]=useState('');
  const [streams, setStreams] = useState<StakingBrainStream[]>([]);
  const [todayData,setTodayData] = useState<StakingBrainPaperData|null>(null);
  const [todayError,setTodayError] = useState('');
  const [expandedStream,setExpandedStream] = useState('');
  const [workerAt,setWorkerAt] = useState<string|null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let active=true;
    const refresh=()=>getStakingBrainStreams().then((result) => {
      if(!active)return;
      setStreams(result.streams.filter(s=>s.state==='ACTIVE_PAPER'));
      setWorkerAt(result.trained_worker_health?.updated_at || null);
      setError('');
    }).catch((err: any) => {if(active)setError(err?.message || 'Could not load streams');});
    const refreshToday=()=>getStakingBrainPaper({date:auDate(),view:'bets'}).then(d=>{if(active){setTodayData(d);setTodayError('');}}).catch(()=>{if(active)setTodayError('Could not load today’s decisions.');});
    const refreshUpcoming=()=>getBrainUpcoming().then(d=>{if(active){setUpcoming(d);setUpcomingError('');}}).catch(()=>{if(active)setUpcomingError('Could not load upcoming selections.');});
    refresh();refreshToday();refreshUpcoming();const timer=window.setInterval(()=>{refresh();refreshToday();refreshUpcoming();},60000);
    return()=>{active=false;window.clearInterval(timer);};
  }, []);
  return (
    <main style={{ maxWidth: 1120, margin: '0 auto', padding: 16 }}>
      <h1 style={{ marginTop: 0 }}>Staking Brain Streams</h1>
      <div className="brain-filter"><label>Brain stream<select value={selectedStream} onChange={e=>{setSelectedStream(e.target.value);setExpandedStream(e.target.value);}}><option value="">All active streams</option>{streams.map(st=><option key={st.stream_id} value={st.stream_id}>{st.name}</option>)}</select></label></div>
      <p><strong>{streams.filter(s=>s.state==='ACTIVE_PAPER').length} active paper streams</strong>{workerAt ? ' · Trained-stream worker checked '+new Date(workerAt).toLocaleString('en-AU',{timeZone:AU_TZ})+' (Sydney)' : ''}</p>
      <p style={{ color: '#59616d' }}>
        Your existing active paper stream is retained. The daily Brain stream is an owner-approved 2026-fitted paper experiment. Its historical and forward results are shown separately.
      </p>
      {error && <p style={{ color: '#c0392b' }}>{error}</p>}
      <section className="brain-note">
        <h2>Daily Brain (3 / Saturday 10) · Today and tomorrow</h2>
        <p>Scanned every 30 minutes through midnight at the end of tomorrow (Sydney). Provisional selections can change as fields update until the daily freeze at 10:30 am Sydney, when the day's picks lock and go to Auto. Up to three qualifiers per day; Saturdays up to ten, ranked by Brain probability. Minimum probability 30%; no forced bets or replacements.</p>
        {upcomingError ? <p role="alert">{upcomingError}</p> : !upcoming ? <p>Loading upcoming selections…</p> : !upcoming.generated_at ? <p>Waiting for the first scan.</p> : <>
          <p>Last scan: {new Date(upcoming.generated_at).toLocaleString('en-AU',{timeZone:AU_TZ})} · Next scan: {upcoming.next_refresh_at ? new Date(upcoming.next_refresh_at).toLocaleTimeString('en-AU',{timeZone:AU_TZ}) : '—'} (Sydney) · {upcoming.checked_races} races checked</p>
          {Date.now()-Date.parse(upcoming.generated_at)>40*60000 ? <p role="alert">This scan is overdue. Selections below are from the last successful scan.</p> : null}
          {upcoming.rows.length ? <div style={{overflowX:'auto'}}><table style={{width:'100%',textAlign:'left',borderCollapse:'collapse'}}>
            <thead><tr>{['Race time (Sydney)','Race','Runner','Brain probability','Status'].map(h=><th key={h} style={{padding:10}}>{h}</th>)}</tr></thead>
            <tbody>{upcoming.rows.map(r=><tr key={r.race_id} style={{borderTop:'1px solid #dde2e8'}}>
              <td style={{padding:10}}>{new Date(r.off_at).toLocaleString('en-AU',{timeZone:AU_TZ})}</td><td>{r.track} R{r.race}</td><td><strong>{r.runner}</strong></td><td>{(r.brain_prob*100).toFixed(1)}%</td><td>{r.selection_status}</td>
            </tr>)}</tbody>
          </table></div> : <p>No qualifying upcoming selections in the current scan through tomorrow.</p>}
        </>}
      </section>
      <section className="brain-note">
        <h2>Today · {auDate()} (Sydney)</h2>
        {todayError ? <p role="alert">{todayError}</p> : !todayData ? <p>Loading today’s paper bets…</p> : todayData.rows.length ? <>
          <p><strong>{todayData.rows.length} recorded paper bet(s) in the original Brain.</strong></p>
          {todayData.rows.map((r,i)=><p key={i}>{r.track} R{r.race} · <strong>{r.runner}</strong> · {r.status} · stake {money(r.applied_stake)}</p>)}
        </> : <p>No bets recorded yet today. This alone does not mean the day was intentionally skipped; the Daily paper bets tab includes BET/PASS reasons.</p>}
      </section>

      <h2>{selectedStream?'Stream results':'Active stream performance'}</h2>
      <p>Both streams report profit and drawdown in benchmark units, with an A$100-per-unit illustration. These simulated dollar amounts are not actual Auto profit or live stake instructions. Auto uses its saved staking method and any manual overrides. Compare report dates and evidence types before comparing results.</p>
      <div style={{ display: 'grid', gap: 10 }}>
        {streams.filter(st=>!selectedStream||st.stream_id===selectedStream).map((stream) => (
          <section key={stream.stream_id + stream.version} style={{
            border: '1px solid #dde2e8', borderRadius: 8, padding: 13, background: '#fff',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <strong>{stream.name} · 1-unit benchmark</strong>
              <span style={{ fontWeight: 700, color:
                stream.state === 'ACTIVE_PAPER' ? '#2d6cdf' :
                stream.state === 'REJECTED' || stream.state === 'RETIRED' ? '#8a3b32' : '#6d5a00' }}>
                {stream.state.replace('_', ' ')}
              </span>
            </div>
            {stream.stream_id === 'daily-slots-v5-4' && <p className="brain-note">Existing active paper stream. Its retrospective results do not establish verified pre-2026 training; retained separately from the qualification rule for new streams.</p>}
            {(() => {
              const e = stream.evaluations.find(e => e.evidence_period === 'HISTORICAL_2026');
              return <><p style={{color:"#59616d",fontSize:13}}>Report period: {e?.from || "Not recorded"} to {e?.to || "Not recorded"} · {e?.priced_bets ?? e?.settled ?? "—"} bets with prices used for ROI. Drawdown follows recorded decision order.</p><div className="performance-tiles">
                <Stat label="2026 ROI" value={e?.roi_pct == null ? 'Unavailable' : e.roi_pct.toFixed(2)+'%'} color={(e?.roi_pct || 0)>0?'#16803c':undefined}/>
                <Stat label="Profit" value={benchmark(e?.pnl_units)}/>
                <Stat label="Strike rate" value={e?.strike_rate_pct == null?'Unavailable':e.strike_rate_pct.toFixed(2)+'%'}/>
                <Stat label="Settled bets" value={String(e?.settled ?? '—')}/>
                <Stat label="Max drawdown" value={benchmark(e?.max_drawdown_units)}/>
              </div></>;
            })()}
            <div style={{ marginTop: 7, color: '#59616d', fontSize: 13 }}>
              Core ranks: {stream.rank_scope_label || stream.rank_universe.join(', ')} · Version: {stream.version} · Origin: {stream.origin}
            </div>
            <p style={{ marginBottom: 6 }}><strong>Objective:</strong>{' '}
              {stream.research_dossier?.objective || stream.hypothesis}</p>
            {stream.evaluation_limitations && <p style={{fontSize:12,color:'#59616d'}}>{stream.evaluation_limitations.join(' ')}</p>}
            {stream.suspension_reason && (
              <div style={{ background: '#fff4e5', border: '1px solid #f4c26b', borderRadius: 6,
                padding: 9, margin: '8px 0', color: '#7a4b00', fontSize: 12 }}>
                <strong>Not currently paper trading.</strong> {stream.suspension_reason.split('_').join(' ')}
              </div>
            )}
            {stream.research_dossier && (
              <details style={{ margin: '8px 0', fontSize: 12 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 700 }}>Research objective, mechanism and pass criteria</summary>
                <div style={{ display: 'grid', gap: 5, marginTop: 7, paddingLeft: 8 }}>
                  <div><strong>Mechanism:</strong> {stream.research_dossier.mechanism}</div>
                  <div><strong>Why created:</strong> {stream.research_dossier.why_created}</div>
                  <div><strong>Selection:</strong> {stream.research_dossier.selection_rule}</div>
                  <div><strong>Execution:</strong> {stream.research_dossier.execution_rule}</div>
                  <div><strong>Evidence required:</strong> {stream.research_dossier.evidence_required}</div>
                  <div><strong>Failure rule:</strong> {stream.research_dossier.failure_rule}</div>
                </div>
              </details>
            )}
            <div style={{ marginBottom: 6, fontSize: 12, color: '#59616d' }}>
              Entry: {stream.execution_price_policy.entry_strategy.split('_').join(' ')}
              {stream.execution_price_policy.maximum ? ` · Maximum $${stream.execution_price_policy.maximum}` : ''}
              {stream.execution_price_policy.minimum ? ` · Minimum $${stream.execution_price_policy.minimum}` : ''}
              {' · Movement: ' + stream.execution_price_policy.predicted_movement}
            </div>
            {(() => {
              const training = stream.evaluations.filter((row) =>
                /^(TRAINING|VALIDATION|HOLDOUT)_/.test(row.evidence_period)).sort((a, b) => (b.year || 0) - (a.year || 0));
              const test2026 = stream.evaluations.find((row) =>
                row.evidence_period === 'HISTORICAL_2026' || row.evidence_period === 'TEST_2026');
              const live = stream.evaluations.find((row) => row.evidence_period === 'PROSPECTIVE_ALL');
              const pct = (value?: number | null) => value == null ? '—' : `${value.toFixed(2)}%`;
              const units = benchmark;
              const resultRow = (label: string, evaluation?: typeof live, muted = false) => (
                <tr key={label} style={{ color: muted ? '#6b7280' : 'inherit' }}>
                  <td style={{ padding: '7px 8px', fontWeight: 700 }}>{label}</td>
                  <td style={{ padding: '7px 8px', textAlign: 'right' }}>{evaluation?.settled ?? 0}</td>
                  <td style={{ padding: '7px 8px', textAlign: 'right' }}>{pct(evaluation?.strike_rate_pct)}</td>
                  <td style={{ padding: '7px 8px', textAlign: 'right' }}>{pct(evaluation?.roi_pct)}</td>
                  <td style={{ padding: '7px 8px', textAlign: 'right',
                    color: (evaluation?.pnl_units || 0) < 0 ? '#b42318' : '#087443' }}>
                    {units(evaluation?.pnl_units)}
                  </td>
                  <td style={{ padding: '7px 8px', textAlign: 'right' }}>
                    {pct(evaluation?.roi_without_top3_wins_pct)}
                  </td>
                  <td style={{ padding: '7px 8px', textAlign: 'right' }}>
                    {units(evaluation?.max_drawdown_units)}
                  </td>
                </tr>
              );
              return (
                <div style={{ overflowX: 'auto', margin: '10px 0' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12,
                    background: '#f7f9fc', borderRadius: 6 }}>
                    <thead><tr style={{ color: '#59616d', borderBottom: '1px solid #dde2e8' }}>
                      <th style={{ padding: '7px 8px', textAlign: 'left' }}>Evidence</th>
                      <th style={{ padding: '7px 8px', textAlign: 'right' }}>Bets</th>
                      <th style={{ padding: '7px 8px', textAlign: 'right' }}>Strike rate</th>
                      <th style={{ padding: '7px 8px', textAlign: 'right' }}>ROI</th>
                      <th style={{ padding: '7px 8px', textAlign: 'right' }}>Profit</th>
                      <th style={{ padding: '7px 8px', textAlign: 'right' }}>ROI ex top 3</th>
                      <th style={{ padding: '7px 8px', textAlign: 'right' }}>Max DD</th>
                    </tr></thead>
                    <tbody>
                      {resultRow(`Prospective since ${stream.created_at?.slice(0, 10) || 'creation'}`, live)}
                      {resultRow('2026 test (retrospective)', test2026,
                        test2026?.evidence_status?.includes('NOT_CERTIFIED'))}
                      {training.length ? training.map((evaluation) =>
                        resultRow(`${evaluation.evidence_period.startsWith('TRAINING')?'Training':evaluation.evidence_period.startsWith('VALIDATION')?'Validation':'Historical evaluation'} ${evaluation.year}`, evaluation)) : (
                        <tr><td colSpan={7} style={{ padding: 8, color: '#8a5a00' }}>
                          No year-by-year pre-2026 training report is attached to this stream.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })()}
            {['historicalPaperBrain','refitDailyBrain'].includes(stream.rule_spec.worker) && <>
              <p style={{fontSize:12}}>2026 priced bets: {stream.evaluations.find(e=>e.evidence_period==='HISTORICAL_2026')?.priced_bets ?? 0}. ROI uses priced bets only; strike rate uses all settled bets. Coverage: {stream.evaluations.find(e=>e.evidence_period==='HISTORICAL_2026')?.from} to {stream.evaluations.find(e=>e.evidence_period==='HISTORICAL_2026')?.to}.</p>
              <details style={{margin:'10px 0'}}><summary>2026 monthly results</summary>
                <table className="sr-table" style={{width:'100%'}}><thead><tr><th>Month</th><th>Wins / settled</th><th>ROI</th><th>Profit</th></tr></thead><tbody>
                {stream.evaluations.find(e=>e.evidence_period==='HISTORICAL_2026')?.monthly?.map(m=><tr key={m.month}><td>{m.month}</td><td>{m.wins} / {m.settled}</td><td>{m.roi_pct==null?'—':m.roi_pct.toFixed(2)+'%'}</td><td>{reportUnits(stream.stream_id,m.pnl_units)}</td></tr>)}
                </tbody></table>
              </details>
              <button style={buttonStyle(expandedStream===stream.stream_id)} onClick={()=>setExpandedStream(expandedStream===stream.stream_id?'':stream.stream_id)}>{expandedStream===stream.stream_id?'Hide bets':'View backdated and paper bets'}</button>
              {expandedStream===stream.stream_id && <HistoricalBrainBets key={stream.stream_id} streamId={stream.stream_id}/>}
            </>}
            {selectedStream===stream.stream_id && stream.stream_id==='daily-slots-v5-4' && <StakingBrainPaper key={stream.stream_id} initialStream={stream.stream_id}/>}
            <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#7b8491' }}>
              SPEC {stream.spec_hash.slice(0, 16)} · PAPER ONLY · NO LIVE AUTHORITY
            </div>
          </section>
        ))}
      </div>
      {!error && !streams.length && <p>No stream data loaded yet.</p>}
    </main>
  );
};

const BrainPage: React.FC = () => {
  const [view, setView] = useState<'backtest' | 'paper' | 'streams' | 'log'>('streams');
  return (
    <div className="brain-workspace">
      <nav aria-label="Brain report" style={{ display: 'flex', gap: 12, padding: 16 }}>
        <button style={buttonStyle(view === 'backtest')} onClick={() => setView('backtest')}>2026 backtest</button>
        <button style={buttonStyle(view === 'paper')} onClick={() => setView('paper')}>Daily paper bets</button>
        <button style={buttonStyle(view === 'streams')} onClick={() => setView('streams')}>Stream results</button>
        <button style={buttonStyle(view === 'log')} onClick={() => setView('log')}>Active stream log</button>
      </nav>
      <BrainLiveGrid />
      {view === 'backtest' ? <BrainBacktest /> :
        view === 'paper' ? <StakingBrainPaper /> : view === 'log' ? <BrainActiveLog /> : <BrainStreams />}
    </div>
  );
};

export default BrainPage;
